// src/pages/LandingPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n'; 
import './LandingPage.css';
import earthVideo from '../assets/earth-loop.mp4';
import AnimatedLogo from '../components/AnimatedLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import posthog from 'posthog-js';
import { useAuth } from '../context/AuthContext';
import api from "../services/api"; // ✅ Make sure this is imported
import HeroVideo from "../components/HeroVideo";
import ReactGA from 'react-ga4';
import { Helmet } from "react-helmet-async";



export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
const { loginWithToken } = useAuth();

  const handleLoginClick = () => {
    posthog.capture('Clicked Login on Landing Page');
     ReactGA.event({
    category: "button",
    action: "click",
    label: "Landing.Login Clicked",
  });
    navigate('/login');
  };


const handleDemoAccess = async () => {
  try {
    const res = await api.post("/auth/demo-login"); // ✅ no payload
    loginWithToken(res.data);
    navigate("/reports/Dep/Report");
    posthog.capture('Clicked Demo on Landing Page');
    ReactGA.event({
    category: "button",
    action: "click",
    label: "Landing.Demo Clicked",
  });
    
  } catch (err) {
    console.error("Demo login failed:", err.response?.data || err.message);
    alert("Demo login failed");
  }
};



  useEffect(() => {
    const videoEl = document.querySelector('.bg-video');
    if (videoEl) videoEl.playbackRate = 0.8;
  }, []);

  return (
         <>
          <Helmet>
            <html lang={i18n.language} />
            <title>{t("meta.landing.title")}</title>
            <meta name="description" content={t("meta.landing.description")} />
              <link
                rel="alternate"
                hrefLang="en"
                href="https://www.insightsflows.com/"
              />
              <link
                rel="alternate"
                hrefLang="ar"
                href="https://www.insightsflows.com/"
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href="https://www.insightsflows.com/"
              />
          </Helmet>
    <div className="landing-root">
      {/* LANGUAGE SWITCH */}
      <LanguageSwitcher />

      {/* HERO */}
      <section className="hero-section">
        <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, padding: '8px', width: 180, height: 180 }}>
          <AnimatedLogo size={180} />
        </div>
        <video className="bg-video" autoPlay muted loop playsInline>
          <source src={earthVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="bg-overlay" />
        <motion.div
          className="glass-box"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.h1 className="hero-title">{t('title')}</motion.h1>
          <motion.p className="hero-subtitle">{t('subtitle')}</motion.p>
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <motion.button
              className="hero-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLoginClick}
            >
              {t('loginButton')}
            </motion.button>
            <motion.button
              className="hero-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDemoAccess}
            >
              {t('tryFree')}
            </motion.button>
          </div>
          <HeroVideo />
        </motion.div>
      </section>

      {/* FEATURES */}
      <section className="features-section">
        <motion.h2 className="section-title" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          {t('whyChooseUs')}
        </motion.h2>
        <div className="features-grid">
          {t('features', { returnObjects: true }).map((feature, idx) => (
            <motion.div key={idx} className="feature-card" initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: idx * 0.2 }} viewport={{ once: true, amount: 0.4 }}>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-it-works">
        <motion.h2 className="section-title" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          {t('howItWorks')}
        </motion.h2>
        <div className="steps">
          {t('steps', { returnObjects: true }).map((step, index) => (
            <motion.div key={index} className="step" initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: index * 0.2 }} viewport={{ once: true }}>
              <span>{index + 1}</span>
              <p>{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials">
        <motion.h2 className="section-title" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          {t('testimonials')}
        </motion.h2>
        <motion.blockquote className="quote" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} viewport={{ once: true }}>
          <p>{t('quote')}</p>
          <footer>{t('quoteAuthor')}</footer>
        </motion.blockquote>
      </section>

      {/* CTA FOOTER */}
      <section className="footer-cta">
        <motion.div initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, bounce: 0.3 }} viewport={{ once: true }}>
          <h2>{t('footerTitle')}</h2>
          <p>{t('footerDesc')}</p>
          <button onClick={handleDemoAccess}>{t('tryFree')}</button>
        </motion.div>
      </section>

      <section name="seosection" className="seo-section max-w-5xl mx-auto px-6 py-16">
  <motion.h2 
    className="seo-heading"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    viewport={{ once: true }}
  >
    {t("seo.heading")}
  </motion.h2>
  <motion.p 
    className="seo-paragraph"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay: 0.1 }}
    viewport={{ once: true }}
  >
    {t("seo.paragraph1")}
  </motion.p>
  <motion.p 
    className="seo-paragraph mt-6"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay: 0.2 }}
    viewport={{ once: true }}
  >
    {t("seo.paragraph2")}
  </motion.p>
</section>


    </div>
    </>

  );
}
